import { snakeCase } from 'change-case';

import type { SearchFilterJoin, SearchFilterOperator } from '@/shared';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const hasValue = (value: unknown): boolean => {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.some(hasValue);
    }
    if (isRecord(value)) {
        return Object.values(value).some(hasValue);
    }

    return true;
};

/** Fields that the API resolves through FMP's company screener boundary. */
export const hasCompanyScreenerCriteria = (filters: Record<string, unknown>): boolean =>
    Boolean(
        hasValue(filters.exchanges) ||
        hasValue(filters.assetClasses) ||
        hasValue(filters.industries) ||
        hasValue(filters.sectors) ||
        hasValue(isRecord(filters.location) ? filters.location.country : undefined) ||
        hasValue(filters.fundamentals) ||
        hasValue(filters.screener),
    );

/**
 * Keep only filters that still require hydrated-result refinement in the web
 * client. Provider-backed criteria have already been applied authoritatively
 * by the API and must not be re-evaluated against incomplete list-item data.
 */
export const getCompanyClientFilters = (filters: Record<string, unknown>): Record<string, unknown> => {
    const { assetClasses, exchanges, fundamentals, industries, screener, sectors, ...clientFilters } = filters;
    const location = isRecord(filters.location)
        ? Object.fromEntries(Object.entries(filters.location).filter(([key]) => key !== 'country'))
        : undefined;

    void assetClasses;
    void exchanges;
    void fundamentals;
    void industries;
    void screener;
    void sectors;

    return {
        ...clientFilters,
        ...(location && hasValue(location) ? { location } : {}),
    };
};

export const convertMonthsToNumbers = (months: string[]) => {
    const monthMap: Record<string, number> = {
        January: 1,
        February: 2,
        March: 3,
        April: 4,
        May: 5,
        June: 6,
        July: 7,
        August: 8,
        September: 9,
        October: 10,
        November: 11,
        December: 12,
    };

    return months.map((month: string) => monthMap[month] || null);
};

export const handleReturnCompanyResultsBasedOnFilters = (
    criteria: any,
    items: any[],
    matchingPattern: SearchFilterJoin = 'any',
    filterOperators: Record<string, SearchFilterOperator> = {},
) => {
    if (!criteria || Object.keys(criteria).length === 0) {
        return items;
    }

    return items.filter((item: any) => {
        const matches = Object.entries(criteria).map(([key, value]: any) => {
            const applyOperator = (matchesFilter: boolean) =>
                filterOperators[key] === 'notEquals' ? !matchesFilter : matchesFilter;

            if (key === 'exchanges') {
                return applyOperator(value?.includes(item.exchange));
            }
            if (key === 'industries') {
                return applyOperator(value?.includes(item?.enrichment?.industry));
            }
            if (key === 'sectors') {
                return applyOperator(value?.includes(item?.enrichment?.sector));
            }
            if (key === 'location') {
                return applyOperator(
                    (hasValue(value?.country) && item?.enrichment?.country === value.country) ||
                    (hasValue(value?.stateTerritory) && item?.enrichment?.state === value.stateTerritory) ||
                    (hasValue(value?.city) && item?.enrichment?.city === value.city) ||
                    (hasValue(value?.zipCode) && item?.enrichment?.zip === value.zipCode),
                );
            }
            if (key === 'fiscalYearEnd') {
                const monthsNumberArray = convertMonthsToNumbers(value);
                return applyOperator(monthsNumberArray.includes(item?.enrichment?.clearbit?.metrics?.fiscalYearEnd));
            }
            if (key === 'employeeHeadcount') {
                const headcount = String(item?.enrichment?.clearbit?.metrics?.employeesRange) || '';
                return applyOperator(
                    value.some((range: string) => headcount.toLowerCase().includes(range.toLowerCase())),
                );
            }
            if (key === 'attributes') {
                return applyOperator(value.some((i: any) => item?.enrichment?.clearbit?.tags?.includes(i)));
            }
            if (key === 'technologies') {
                const { individualTechnologies, entireCategories } = value;
                const tech = item?.enrichment?.clearbit?.tech || [];
                const techCategories = item?.enrichment?.clearbit?.techCategories || [];
                return applyOperator(
                    tech.some((t: any) => individualTechnologies.includes(t)) ||
                        techCategories.some((c: any) => entireCategories.includes(c)),
                );
            }
            if (key === 'webTraffic') {
                return applyOperator(
                    value.some((i: any) => snakeCase(i) === item?.enrichment?.clearbit?.metrics?.trafficRank),
                );
            }
            return applyOperator(item[key] === value);
        });
        return matchingPattern === 'all' ? matches.every(Boolean) : matches.some(Boolean);
    });
};

export function findDifferencesBetweenFilters(obj1: any, obj2: any, path = ''): any[] {
    const differences: any[] = [];

    for (const key in obj1) {
        const newPath = path ? `${path}.${key}` : key;
        const value1 = obj1[key];
        const value2 = obj2[key];

        if (Array.isArray(value1) && Array.isArray(value2)) {
            const addedValues = value2.filter((v) => !value1.includes(v));
            if (addedValues.length > 0) {
                differences.push({
                    path: newPath,
                    oldValue: value1,
                    newValue: value2,
                });
            }
        } else if (typeof value1 === 'object' && typeof value2 === 'object') {
            differences.push(...findDifferencesBetweenFilters(value1, value2, newPath));
        } else if (value1 !== value2) {
            differences.push({
                path: newPath,
                oldValue: value1,
                newValue: value2,
            });
        }
    }

    // Handle properties that might exist in obj2 but not in obj1
    for (const key in obj2) {
        if (!(key in obj1)) {
            const newPath = path ? `${path}.${key}` : key;
            differences.push({
                path: newPath,
                oldValue: undefined,
                newValue: obj2[key],
            });
        }
    }

    return differences;
}
